// P0-4 (docs/design/ux-simplify.md §5): "why is this destination not on
// air" - ONE answer, the same words on every screen that shows a
// destination (Подачи, Линии, Рестрим; Выходы and the channel card can take
// it from here, see docs/contract-requests/UIX-RCFG.md).
//
//   nothing connects a channel     -> "Ничего не подключено"          [Подключить канал]
//   the route is off               -> "Подключено к CH0, выключено"       [Включить]
//   the route is armed             -> "Подключено к CH0, не в эфире"      [В эфир]
//   (these two word for word as on the Outputs wall, outwall/model.ts)
//   on air, control holds it back  -> "Не в эфире — по расписанию · CH0"
//   on air, the leg is not up      -> "Не можем достучаться до адресата: отказ по паролю"
//   on air, the agent confirms     -> "В эфире" ("В эфире заставка", "Видео закончилось, в эфире заставка")
//   on air, degraded, legs all up  -> "В эфире, с неполадками"
//   the agent failed / restarts it -> "Не выходит в эфир — отказ · CH0"
//
// The route is the operator's order (Route.state), RouteStatus is what the
// agent does (effective + phase), a LegStatus is one destination's socket.

import type { ChannelStatus, ID, Leg, LegStatus, Line, Route, RouteState, RouteStatus } from '@/api/types'
import { downReasonLabel } from '@/features/outwall/model'
import { phaseLabel, type Tone } from '@/shared/components/status/tone'
import { messages } from './i18n'
import { REASON_LABEL } from './model'

/** What the operator can do about it; the screen maps it to a button or a link. */
export type AirAction = 'connect' | 'enable' | 'on_air' | 'legs'

export interface AirStatus {
  /** 'air' | 'ok' | 'warn' | 'alarm' | 'idle' - the chip colour. */
  tone: Tone
  /** The answer in words, the channel named where there is one. */
  text: string
  /** The next step, when there is an obvious one. */
  action?: AirAction
  /** A technical detail (the agent's last error) for a tooltip, never the main text. */
  detail?: string
  /** Sort key: 0 = on air, 1 = expected to be on air but is not, 2 = parked, 3 = nothing connected. */
  rank: number
}

export interface AirInput {
  /** The route connecting a channel to the destination, if any. */
  route?: Pick<Route, 'state'> | null
  /** Its channel's name (the id when the name is not known). */
  channelName?: string
  /** The agent's status of that route. */
  status?: RouteStatus | null
  /**
   * The destination's own socket. Omitted = the route as a whole (every leg
   * it feeds): then "not up" means no leg is connected.
   */
  leg?: LegStatus | null
  /**
   * What the channel puts on air instead of the programme: 'ended' = its URL
   * input has ended and the slate plays, 'slate' = the slate plays. See
   * channelSlate: the Restream card says "видео закончилось", the Routes and
   * Lines screens must say the same for the same route.
   */
  slate?: 'ended' | 'slate'
  /**
   * The channel's own config_refused_locally alarm, translated (Д4, UIX-
   * прогон 26.09): when the agent refuses the channel's whole configuration
   * (no free slot and the like), `status.reason` still defaults to 'base'
   * (none of window/hold/override/standby/conflict fit), which read as
   * "задано вручную" ("set manually") - true of nothing an operator did.
   * This is the real answer and wins over that default.
   */
  refusedReason?: string | null
}

/** The slate state of a channel for airStatus, from the agent's channel status. */
export function channelSlate(ch: Pick<ChannelStatus, 'inputs' | 'on_slate'> | undefined): AirInput['slate'] {
  if (!ch) return undefined
  const live = ch.inputs.some((i) => i.active && i.connected && i.phase !== 'ended')
  if (!live && ch.inputs.some((i) => i.phase === 'ended')) return 'ended'
  return ch.on_slate ? 'slate' : undefined
}

const t = messages.t
type Key = Parameters<typeof messages.t>[0]

/** The label of a route state in the words of the dictionary (§2), not "Готова". */
export function routeStateWords(state: RouteState): string {
  switch (state) {
    case 'on_air':
      return t('stateOnAir')
    case 'armed':
      return t('stateArmed')
    case 'off':
      return t('stateOff')
  }
}

export function airStatus(i: AirInput): AirStatus {
  const route = i.route
  if (!route) return { tone: 'idle', text: t('airNothing'), action: 'connect', rank: 3 }
  // Named where the channel is not on the same line of the screen already
  // (a Routes row has it in its first column: no channel, short words).
  const named = i.channelName !== undefined
  const channel = i.channelName ?? ''
  // The state first, the channel after it: a chip cut by an ellipsis keeps
  // the answer and loses the tail of a long name, never the other way round.
  const say = (short: Key, p: Record<string, string> = {}): string => (named ? t('airNamed', { state: t(short, p), channel }) : t(short, p))
  const st = i.status

  // What the agent does wins over the order: a schedule window can put an
  // "off" route on air and a standby site can hold an "on air" one back.
  const effective = st ? st.effective : route.state
  if (effective !== 'on_air') {
    if (route.state === 'on_air' && st) {
      const why = i.refusedReason ?? REASON_LABEL[st.reason] ?? st.reason
      return { tone: 'warn', text: say('airHeldShort', { reason: why }), rank: 1 }
    }
    if (effective === 'armed') return { tone: 'idle', text: named ? t('airArmed', { channel }) : t('stateArmed'), action: 'on_air', rank: 2 }
    return { tone: 'idle', text: named ? t('airOff', { channel }) : t('stateOff'), action: 'enable', rank: 2 }
  }
  if (!st) return { tone: 'idle', text: say('airNoDataShort'), rank: 1 }

  const unreachable = (legs: readonly LegStatus[]): AirStatus => {
    const reasonOf = legs.find((l) => l.down_reason)?.down_reason
    const reason = reasonOf ? downReasonLabel(reasonOf) : ''
    return {
      tone: 'alarm',
      text: reason ? t('airUnreachableWhy', { reason }) : t('airUnreachable'),
      action: 'legs',
      detail: legs.find((l) => l.last_error)?.last_error || undefined,
      rank: 1,
    }
  }
  // On air with the programme, or with the slate in its place - named like
  // "Подключено к CH0, …" (Линии, Цели: "В эфире · CH0").
  const onAir = (): AirStatus => {
    if (i.slate === 'ended') return { tone: 'warn', text: say('airEnded'), rank: 1 }
    if (i.slate === 'slate') return { tone: 'warn', text: say('airOnSlate'), rank: 1 }
    return { tone: 'air', text: say('airOnAir'), rank: 0 }
  }
  // The agent failed the route or is restarting it: never "going on air".
  const broken = (): AirStatus => ({
    tone: 'alarm',
    text: say('airFailedShort', { phase: phaseLabel(st.phase).toLowerCase() }),
    action: 'legs',
    rank: 1,
  })
  if (i.leg !== undefined) {
    const leg = i.leg
    if (leg?.connected) return onAir()
    if (leg) return unreachable([leg])
    if (st.phase === 'failed' || st.phase === 'backoff') return broken()
    return { tone: 'warn', text: say('airStartingShort'), rank: 1 }
  }
  const legs = st.legs ?? []
  if (legs.length > 0 && !legs.some((l) => l.connected)) return unreachable(legs)
  if (legs.some((l) => !l.connected)) return { tone: 'warn', text: t('airPartly'), action: 'legs', rank: 1 }
  switch (st.phase) {
    case 'running':
      return onAir()
    case 'degraded':
      // Degraded with every leg up (audio bypass, a strict input down): the
      // programme is on air, with a fault the tooltip names.
      return i.slate ? onAir() : { tone: 'warn', text: say('airTrouble'), detail: phaseLabel(st.phase), rank: 1 }
    case 'failed':
    case 'backoff':
      return broken()
    default:
      return { tone: 'warn', text: say('airStartingShort'), rank: 1 }
  }
}

/** Of several routes to one destination, the one that answers for it: the loudest state first. */
export function answeringRoute<T extends Pick<Route, 'state'>>(routes: readonly T[]): T | undefined {
  const rank: Record<RouteState, number> = { on_air: 3, armed: 2, off: 1 }
  let best: T | undefined
  for (const r of routes) if (!best || rank[r.state] > rank[best.state]) best = r
  return best
}

/**
 * The answer for one destination (a leg of a line): the route that covers it
 * (all legs, or this one among leg_ids), its channel and the agent's status of
 * that leg. Lines and Targets both show it under each destination.
 */
export function destinationAir(
  line: Pick<Line, 'id'>,
  leg: Pick<Leg, 'id'>,
  routes: readonly Route[],
  channelNames: ReadonlyMap<ID, string>,
  statusOf: (r: Route) => RouteStatus | undefined,
  slateOf: (r: Route) => AirInput['slate'] = () => undefined,
  // Д4 (UIX-прогон 26.09): the same config_refused_locally reason Routes
  // shows (RoutesView.vue) - optional so a caller that has not wired an
  // alarms lookup yet keeps the old (misleading "задано вручную") behaviour
  // rather than breaking.
  refusedReasonOf: (channelId: ID) => string | null = () => null,
): AirStatus {
  const covering = routes.filter((r) => r.line_id === line.id && (!r.leg_ids || r.leg_ids.length === 0 || r.leg_ids.includes(leg.id)))
  const route = answeringRoute(covering)
  if (!route) return airStatus({})
  const status = statusOf(route)
  return airStatus({
    route,
    channelName: channelNames.get(route.channel_id) ?? route.channel_id,
    status,
    refusedReason: refusedReasonOf(route.channel_id),
    // No per-leg list in the status (RouteStatus.legs is optional): the route
    // answers as a whole, the same words the Routes screen shows.
    leg: status?.legs ? (status.legs.find((l) => l.leg_id === leg.id) ?? null) : undefined,
    slate: slateOf(route),
  })
}
